import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import ProfileMenu from "./components/ProfileMenu";
import Dashboard from "./components/Dashboard";
import BoardsView from "./components/BoardsView";
import BoardDetail from "./components/BoardDetail";
import UsersView from "./components/UsersView";
import AppConfigView from "./components/AppConfigView";
import DatasourceView from "./components/DatasourceView";
import ReportsView from "./components/ReportsView";
import SchedulingView from "./components/SchedulingView";
import TicketsView from "./components/TicketsView";
import RequirementBoardView from "./components/RequirementBoardView";
import AppPageView from "./components/AppPageView";
import AppPageExecutionsView from "./components/AppPageExecutionsView";
import ExecutionsView from "./components/ExecutionsView";
import ForYouView from "./components/ForYouView";
import BuildPlaywrightScriptsPage from "./components/BuildPlaywrightScriptsPage";
import SettingsView from "./components/SettingsView";
import { useTheme } from "./theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Normalize a board from the API into the shape the UI expects. */
function boardFromApi(b) {
  return {
    id: b.board_id,
    name: b.name,
    description: b.description || "No description provided.",
    accent: b.accent || "from-brand-600 to-brand-800",
    total: b.total ?? 0,
    passed: b.passed ?? 0,
    failed: b.failed ?? 0,
    pending: b.pending ?? 0,
    // Kept for components that still read board.testCases; counts above are
    // authoritative for list summaries.
    testCases: [],
  };
}

/* Top header bar for the authenticated app shell. */
function Header({ onLogout, onOpenSettings }) {
  // The bottom border sits on the h-16 row, not on the <header>. box-sizing:
  // border-box only absorbs a border when the height is set on the same
  // element, so with the border on the outer element the header measured 65px
  // — one pixel more than the 4rem that every `h-[calc(100vh-8rem)]` screen
  // assumes. That single pixel of overflow gave the whole app a permanent page
  // scrollbar on screens with nothing to scroll.
  return (
    <header className="sticky top-0 z-30 bg-gradient-to-r from-brand-800 to-brand-700 text-white shadow-sm">
      <div className="flex h-16 items-center justify-between border-b border-brand-800/40 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="CortexQA logo"
            className="h-8 w-auto object-contain"
          />
          <div className="leading-tight">
            <p className="text-base font-bold tracking-tight">CortexQA</p>
            <p className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-brand-200/80">
              AI Testing Server
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
        <ProfileMenu onLogout={onLogout} onOpenSettings={onOpenSettings} />
        </div>
      </div>
    </header>
  );
}

export default function MainPage({ onLogout }) {
  const { userkey } = useTheme();
  const [nav, setNav] = useState("dashboard");
  // Views that must survive navigation (a live AI session, ticket filters…) are
  // mounted on first visit and then only hidden — unmounting them would drop
  // the chat/WebSocket and reset the user's context.
  const [keptMounted, setKeptMounted] = useState({ tickets: false, requirements: false, apppage: false });

  /* Bumped when a deep-linked authoring flow hands control back, so the
     kept-mounted Requirement Board re-reads what that flow just created. */
  const [boardRefreshSeq, setBoardRefreshSeq] = useState(0);
  /* Set by the Requirement Board's "+ Create Playwright Scripts" button —
     drives the single-screen BuildPlaywrightScriptsPage. Shape:
     { projectKey, epicKey, epicSummary, story }. */
  const [playwrightCtx, setPlaywrightCtx] = useState(null);
  /* Set when a Playwright generation is saved: re-opens the Requirement Board
     on the epic / story / tests it came from, with the generated scripts
     waiting for Save / Cancel / Go to execution. Shape:
     { seq, epicKey, storyKey, xrayTestKeys, cases }. */
  const [boardReturn, setBoardReturn] = useState(null);
  useEffect(() => {
    if (nav !== "tickets" && nav !== "requirements" && nav !== "apppage") return;
    setKeptMounted((m) => (m[nav] ? m : { ...m, [nav]: true }));
  }, [nav]);
  const [boards, setBoards] = useState([]);
  const [openBoardId, setOpenBoardId] = useState(null);
  const [subsByBoard, setSubsByBoard] = useState({}); // { [boardId]: [subcategory] }
  const [openSubKey, setOpenSubKey] = useState(null); // sub-category drilled into (sidebar)
  // When opening a test case from the Tickets page "View" button: which board
  // to open and which case's run view to show. Shape: { boardId, testcasekey }.
  const [viewCaseTarget, setViewCaseTarget] = useState(null);
  // When opening an execution from the Tickets page "Run" action: which
  // execution to open in the Executions view and whether to auto-start it.
  // Shape: { executionkey, autoRun }.
  const [execTarget, setExecTarget] = useState(null);

  const openBoard = boards.find((b) => b.id === openBoardId) || null;

  // Fetch every board's sub-categories so the sidebar can nest them.
  const loadSubs = useCallback(async (boardList) => {
    try {
      const entries = await Promise.all(
        (boardList || []).map(async (b) => {
          try {
            const r = await fetch(
              `${API_BASE_URL}boards/${encodeURIComponent(b.id)}/subcategories`
            );
            const body = await r.json().catch(() => ({}));
            return [b.id, r.ok && Array.isArray(body?.data) ? body.data : []];
          } catch {
            return [b.id, []];
          }
        })
      );
      setSubsByBoard(Object.fromEntries(entries));
    } catch {
      /* leave existing map */
    }
  }, []);

  const loadBoards = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}boards`);
      const body = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(body?.data)) {
        const list = body.data.map(boardFromApi);
        setBoards(list);
        loadSubs(list);
      }
    } catch {
      // Network error — leave the current list as-is.
    }
  }, [loadSubs]);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  const showDashboard = () => {
    setNav("dashboard");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  /* Bumped when Create Artifact (opened from For you) hands back, so the
     Test Execution there shows the new Artifact's Execute button. */
  const [forYouRefreshSeq, setForYouRefreshSeq] = useState(0);
  const showForYou = () => {
    setNav("foryou");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showBoardsList = () => {
    setNav("boards");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showUsers = () => {
    setNav("users");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showAppConfig = () => {
    setNav("appconfig");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showDatasource = () => {
    setNav("datasource");
    setOpenBoardId(null);
  };

  const showReports = () => {
    setNav("reports");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showScheduling = () => {
    setNav("scheduling");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showTickets = () => {
    setNav("tickets");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showRequirements = () => {
    setNav("requirements");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showAppPage = () => {
    setNav("apppage");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showAppPageExecutions = () => {
    setNav("apppageExecutions");
    setOpenBoardId(null);
    setOpenSubKey(null);
  };

  const showExecutions = () => {
    setNav("executions");
    setOpenBoardId(null);
    setOpenSubKey(null);
    setViewCaseTarget(null);
    setExecTarget(null);
    setRunsTarget(null);
  };

  /* "Save & Execute" on the Requirement Board: open Test Runs on that epic and
     test. `seq` remounts the view so it picks the new target up. */
  const [runsTarget, setRunsTarget] = useState(null);
  const openTestRuns = (target) => {
    setNav("executions");
    setOpenBoardId(null);
    setOpenSubKey(null);
    setViewCaseTarget(null);
    setExecTarget(null);
    setRunsTarget({ ...target, seq: Date.now() });
  };

  /* Settings (from the user menu) opens on its Profile tab. `seq` remounts
     it, so each open lands there. */
  const [settingsTarget, setSettingsTarget] = useState({ tab: "profile", seq: 0 });
  const showSettings = (tab = "profile") => {
    setNav("settings");
    setOpenBoardId(null);
    setOpenSubKey(null);
    setSettingsTarget({ tab, seq: Date.now() });
  };
  const showProfile = () => showSettings("profile");

  // From the Tickets page "View" button: open the case's board and jump
  // straight into its run/view screen (the same full-page RunView the board
  // uses). BoardDetail reads initialViewCaseId to open it on mount.
  const openTestCaseView = (boardId, testcasekey) => {
    if (!boardId || !testcasekey) return;
    setNav("boards");
    setOpenBoardId(boardId);
    setOpenSubKey(null);
    setViewCaseTarget({ boardId, testcasekey });
  };

  // From the Tickets page "Run" action: jump to the Executions view with this
  // execution opened, and auto-start its run.
  const openExecutionRun = (executionkey) => {
    if (!executionkey) return;
    setNav("executions");
    setOpenBoardId(null);
    setOpenSubKey(null);
    setViewCaseTarget(null);
    setExecTarget({ executionkey, autoRun: true });
  };

  const addBoard = async ({ name, description }) => {
    try {
      const res = await fetch(`${API_BASE_URL}boards`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, description: description || "", userkey: userkey || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.board_id) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      await loadBoards();
      setNav("boards");
      setOpenBoardId(body.data.board_id); // jump into the new board
    } catch (e) {
      alert(`Could not create test suite: ${e.message}`);
    }
  };

  const deleteBoard = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}boards/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      // If the deleted board is currently open, return to the boards list.
      if (openBoardId === id) {
        setOpenBoardId(null);
        setOpenSubKey(null);
        setNav("boards");
      }
      await loadBoards();
    } catch (e) {
      alert(`Could not delete test suite: ${e.message}`);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <Header
        onLogout={onLogout}
        onOpenSettings={showProfile}
      />

      <div className="flex min-h-0 flex-1">
        <Sidebar
          active={nav}
          boards={boards}
          subsByBoard={subsByBoard}
          activeBoardId={openBoardId}
          activeSubKey={openSubKey}
          onSelectDashboard={showDashboard}
          onSelectForYou={showForYou}
          onSelectBoards={showBoardsList}
          onOpenBoard={(id) => {
            setNav("boards");
            setOpenBoardId(id);
            setOpenSubKey(null);
            setViewCaseTarget(null);
          }}
          onOpenSubcategory={(boardId, subKey) => {
            setNav("boards");
            setOpenBoardId(boardId);
            setOpenSubKey(subKey);
            setViewCaseTarget(null);
          }}
          onSelectUsers={showUsers}
          onSelectAppConfig={showAppConfig}
          onSelectDatasource={showDatasource}
          onSelectReports={showReports}
          onSelectScheduling={showScheduling}
          onSelectTickets={showTickets}
          onSelectRequirements={showRequirements}
          onSelectAppPage={showAppPage}
          onSelectAppPageExecutions={showAppPageExecutions}
          onSelectExecutions={showExecutions}
        />

        <main className="min-w-0 flex-1 overflow-y-auto">
          {/* Content width.

              This was capped at a flat 1280px, which left ~640px of empty page on a
              1080p monitor while the Requirement Board's columns were squeezed.
              It now steps up with the display instead of being one fixed size:

                laptop (≤1536px)  the cap never binds — full width less padding
                monitor (1920px)  1700px, with comfortable gutters
                large / TV (≥1920) 2000px

              Still capped rather than w-full: on a 4K TV an uncapped row would
              run text across 3800px, which is harder to read, not easier.

              py-8 is load-bearing — header (h-16) + this padding is the 8rem
              that every `h-[calc(100vh-8rem)]` screen subtracts. Changing it
              breaks the full-height layouts, so only the horizontal padding
              grows here. */}
          <div className="mx-auto w-full max-w-[106.25rem] px-4 py-8 sm:px-6 lg:px-6 xl:px-10 3xl:max-w-[125rem] 4xl:max-w-[200rem] 4xl:px-14">
            {nav === "dashboard" && (
              <Dashboard
                boards={boards}
                onOpenBoard={(id) => {
                  setNav("boards");
                  setOpenBoardId(id);
                  setViewCaseTarget(null);
                }}
                onViewAllBoards={showBoardsList}
                onViewUsers={showUsers}
              />
            )}
            {nav === "foryou" && (
              <ForYouView
                userkey={userkey}
                refreshSeq={forYouRefreshSeq}
                onCreateArtifact={(ctx) => {
                  setPlaywrightCtx({ ...ctx, returnTo: "foryou" });
                  setNav("playwrightBuild");
                }}
                onOpenArtifacts={openTestRuns}
              />
            )}
            {nav === "boards" && !openBoard && (
              <BoardsView
                boards={boards}
                onOpenBoard={(id) => {
                  setViewCaseTarget(null);
                  setOpenBoardId(id);
                }}
                onAddBoard={addBoard}
                onDeleteBoard={deleteBoard}
              />
            )}
            {nav === "boards" && openBoard && (
              <BoardDetail
                key={`${openBoard.id}:${
                  viewCaseTarget && viewCaseTarget.boardId === openBoard.id
                    ? viewCaseTarget.testcasekey
                    : ""
                }`}
                board={openBoard}
                initialSubKey={openSubKey}
                initialViewCaseId={
                  viewCaseTarget && viewCaseTarget.boardId === openBoard.id
                    ? viewCaseTarget.testcasekey
                    : null
                }
                onActiveSubChange={setOpenSubKey}
                onSubcategoriesChanged={() => loadSubs(boards)}
                onDeleteBoard={deleteBoard}
                onBack={() => {
                  setOpenBoardId(null);
                  setOpenSubKey(null);
                }}
              />
            )}
            {nav === "users" && <UsersView />}
            {keptMounted.requirements && (
              <div className={nav === "requirements" ? "" : "hidden"}>
                <RequirementBoardView
                  userkey={userkey}
                  visible={nav === "requirements"}
                  onCreatePlaywright={(ctx) => {
                    /* Single-screen flow — bypass the ticket-picker +
                       CreateTestsModal + CreateTestCasePage chain since the
                       Requirement Board already knows which story to automate. */
                    setPlaywrightCtx(ctx);
                    setNav("playwrightBuild");
                  }}
                  refreshSeq={boardRefreshSeq}
                  returnTo={boardReturn}
                  onOpenTestRuns={openTestRuns}
                  /* Execution creation is handled on the board itself now: it
                     already knows the epic, story and tests, so a dialog there
                     beats navigating to an empty Executions form. */
                />
              </div>
            )}
            {/* Kept mounted so a generation in progress keeps its WebSocket. */}
            {keptMounted.apppage && (
              <div className={nav === "apppage" ? "" : "hidden"}>
                <AppPageView userkey={userkey} />
              </div>
            )}
            {nav === "apppageExecutions" && <AppPageExecutionsView userkey={userkey} />}
            {nav === "playwrightBuild" && playwrightCtx && (
              <BuildPlaywrightScriptsPage
                ctx={playwrightCtx}
                onCancel={() => {
                  if (playwrightCtx.returnTo === "foryou") {
                    setNav("foryou");
                    setPlaywrightCtx(null);
                    return;
                  }
                  /* Back to where the user left: same epic and story. */
                  if (playwrightCtx.story?.key) {
                    setBoardReturn({
                      seq: Date.now(),
                      epicKey: playwrightCtx.epicKey || playwrightCtx.story.epic_key || "",
                      storyKey: playwrightCtx.story.key,
                    });
                  }
                  setNav("requirements");
                  setPlaywrightCtx(null);
                }}
                onDone={(result) => {
                  if (playwrightCtx.returnTo === "foryou") {
                    /* Generate's Save already saved the Artifact: back to the
                       Test Execution, where it can now be executed. */
                    setNav("foryou");
                    setPlaywrightCtx(null);
                    setForYouRefreshSeq((n) => n + 1);
                    setBoardRefreshSeq((n) => n + 1);
                    return;
                  }
                  setNav("requirements");
                  setPlaywrightCtx(null);
                  if (result?.storyKey) setBoardReturn({ ...result, seq: Date.now() });
                  /* Nudge the board so its cached Xray coverage shows the
                     brand-new Playwright scripts without a hard reload. */
                  setBoardRefreshSeq((n) => n + 1);
                }}
              />
            )}
            {nav === "appconfig" && <AppConfigView />}
            {nav === "datasource" && <DatasourceView />}
            {nav === "reports" && <ReportsView boards={boards} userkey={userkey} />}
            {nav === "scheduling" && <SchedulingView boards={boards} userkey={userkey} />}
            {keptMounted.tickets && (
              <div className={nav === "tickets" ? "" : "hidden"}>
                <TicketsView
                  onOpenTestCase={openTestCaseView}
                  onRunExecution={openExecutionRun}
                  userkey={userkey}
                />
              </div>
            )}
            {nav === "executions" && (
              <ExecutionsView
                key={execTarget ? `exec:${execTarget.executionkey}` : runsTarget ? `runs:${runsTarget.seq}` : "exec:list"}
                userkey={userkey}
                boards={boards}
                initialTarget={runsTarget}
                initialExecutionKey={execTarget?.executionkey || null}
                autoRun={execTarget?.autoRun || false}
              />
            )}
            {nav === "settings" && (
              <SettingsView
                key={settingsTarget.seq}
                userkey={userkey}
                initialTab={settingsTarget.tab}
                onBack={showDashboard}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
